/**
 * OP-5: Live Tracking Page
 * Full-screen map with active work orders and colored markers
 * Markers: Warehouse (purple), Client (orange), Device OK (green), Too Hot (red), Too Cold (blue), Rider (yellow)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { 
  RefreshCw, Loader2, Package, Thermometer, MapPin, 
  Battery, Clock, ChevronRight, Building2, Store, Truck
} from 'lucide-react';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Color markers for different types
const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom SVG icons for special markers
const createCustomIcon = (color, type) => {
  let svgIcon;
  if (type === 'warehouse') {
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="32" height="32"><path d="M22 21V7L12 2 2 7v14h20zm-2-2h-4v-5h-8v5H4V8.4l8-4.5 8 4.5V19zM8 19h8v-3H8v3z"/></svg>`;
  } else if (type === 'client') {
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="32" height="32"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
  }
  
  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Marker icons
const ICONS = {
  warehouse: createIcon('violet'),      // Purple - Pickup/Warehouse
  client: createIcon('orange'),         // Orange - Client locations
  deviceOk: createIcon('green'),        // Green - Device (Temp OK: 2-8°C)
  deviceHot: createIcon('red'),         // Red - Device (Too Hot)
  deviceCold: createIcon('blue'),       // Blue - Device (Too Cold)
  rider: createIcon('yellow'),          // Yellow - Rider/Trip
  offline: createIcon('grey'),          // Grey - Offline device
};

// Get device marker based on temperature
const getDeviceIcon = (temp, isOnline) => {
  if (!isOnline) return ICONS.offline;
  if (temp === null || temp === undefined) return ICONS.rider;
  const tempNum = parseFloat(temp);
  if (tempNum < 2) return ICONS.deviceCold;    // Too Cold
  if (tempNum > 8) return ICONS.deviceHot;     // Too Hot
  return ICONS.deviceOk;                        // OK (2-8°C)
};

// Get temperature status text
const getTempStatus = (temp) => {
  if (temp === null || temp === undefined) return { text: 'N/A', color: 'text-slate-400' };
  const tempNum = parseFloat(temp);
  if (tempNum < 2) return { text: 'Too Cold', color: 'text-blue-400' };
  if (tempNum > 8) return { text: 'Too Hot', color: 'text-red-400' };
  return { text: 'OK', color: 'text-green-400' };
};

// Map center controller
function MapCenterController({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 12);
    }
     
  }, [center, map]);
  return null;
}

export default function LiveTrackingPage() {
  const navigate = useNavigate();
  const { tenant } = useTrackOticAuth();
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [deviceHealth, setDeviceHealth] = useState({ devices: [] });
  const [locations, setLocations] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [mapCenter, setMapCenter] = useState([28.52, 77.22]); // Delhi NCR default
  const [features, setFeatures] = useState({ temp: { enabled: true }, gps: { enabled: true } });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchDataRefresh, 15000);
    return () => clearInterval(interval);
     
  }, []);

  const fetchData = async () => {
    try {
      const [woRes, healthRes, inTransitRes, featuresRes, locRes] = await Promise.all([
        opsApi.getWorkOrders({ status: 'started' }),
        opsApi.getDeviceHealth({}),
        opsApi.getWorkOrders({ status: 'in_transit' }),
        opsApi.getTenantFeatures(),
        opsApi.getLocations().catch(() => ({ data: [] }))
      ]);
      
      const activeWOs = [...woRes.data, ...inTransitRes.data];
      
      setWorkOrders(activeWOs);
      setDeviceHealth(healthRes.data);
      setFeatures(featuresRes.data);
      setLocations(locRes.data || []);
      
      // Auto-center on first device or work order
      const firstDevice = healthRes.data.devices?.find(d => d.current_lat && d.current_lng);
      if (firstDevice) {
        setMapCenter([parseFloat(firstDevice.current_lat), parseFloat(firstDevice.current_lng)]);
      } else if (activeWOs.length > 0 && activeWOs[0].pickup_lat) {
        setMapCenter([parseFloat(activeWOs[0].pickup_lat), parseFloat(activeWOs[0].pickup_lng)]);
      }
    } catch (error) {
        // Silently handled
    } finally {
      setLoading(false);
    }
  };

  const fetchDataRefresh = async () => {
    try {
      const [woRes, healthRes, inTransitRes] = await Promise.all([
        opsApi.getWorkOrders({ status: 'started' }),
        opsApi.getDeviceHealth({}),
        opsApi.getWorkOrders({ status: 'in_transit' })
      ]);
      
      const activeWOs = [...woRes.data, ...inTransitRes.data];
      setWorkOrders(activeWOs);
      setDeviceHealth(healthRes.data);
    } catch (error) {
        // Silently handled
    }
  };

  const getDevicePosition = (deviceId) => {
    const device = deviceHealth.devices?.find(d => d.id === deviceId);
    if (!device || !device.current_lat || !device.current_lng) return null;
    
    return {
      lat: parseFloat(device.current_lat),
      lng: parseFloat(device.current_lng),
      temp: device.current_temp ? parseFloat(device.current_temp) : null,
      battery: device.battery_level,
      isOnline: device.status === 'active' || device.status === 'online',
      deviceUid: device.device_uid,
      capabilities: device.capabilities || {}
    };
  };

  const handleSelectWO = (wo) => {
    setSelectedWO(wo);
    const devicePos = getDevicePosition(wo.assigned_device_id);
    if (devicePos) {
      setMapCenter([devicePos.lat, devicePos.lng]);
    } else if (wo.pickup_lat && wo.pickup_lng) {
      setMapCenter([parseFloat(wo.pickup_lat), parseFloat(wo.pickup_lng)]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_transit': return 'amber';
      case 'started': return 'blue';
      case 'completed': return 'green';
      case 'failed': return 'red';
      default: return 'slate';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-teal-500" />
          <p className="text-slate-400">Loading live tracking...</p>
        </div>
      </div>
    );
  }

  // Get all devices with positions
  const devicesWithPositions = deviceHealth.devices?.filter(d => d.current_lat && d.current_lng) || [];

  return (
    <div className="h-[calc(100vh-4rem)]">
      <div className="h-full flex">
        {/* Sidebar */}
        <div className="w-96 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">Live Tracking</h1>
                <p className="text-slate-500 dark:text-slate-400 text-sm">{workOrders.length} active shipments</p>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={fetchData}
                className="border-slate-300 dark:border-slate-700"
                data-testid="tracking-refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            
            <Select defaultValue="all">
              <SelectTrigger className="mt-3 bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white" data-testid="tracking-filter-select">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <SelectItem value="all">All Active</SelectItem>
                <SelectItem value="in_transit">In Transit</SelectItem>
                <SelectItem value="started">Started</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Work Orders List */}
          {workOrders.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No active shipments</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto divide-y divide-slate-200 dark:divide-slate-800">
              {workOrders.map(wo => {
                const devicePos = getDevicePosition(wo.assigned_device_id);
                const isSelected = selectedWO?.id === wo.id;
                const tempStatus = devicePos ? getTempStatus(devicePos.temp) : { text: 'N/A', color: 'text-slate-400' };
                
                return (
                  <button
                    key={wo.id}
                    onClick={() => handleSelectWO(wo)}
                    className={`w-full p-4 text-left transition-colors ${
                      isSelected ? 'bg-teal-100 dark:bg-teal-900/30' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                    data-testid={`tracking-wo-${wo.work_order_number}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-slate-900 dark:text-white text-sm">{wo.work_order_number}</span>
                      <Badge 
                        variant="outline" 
                        className={`text-xs bg-${getStatusColor(wo.status)}-500/20 text-${getStatusColor(wo.status)}-400 border-${getStatusColor(wo.status)}-500`}
                      >
                        {wo.status}
                      </Badge>
                    </div>
                    
                    <div className="text-slate-500 dark:text-slate-400 text-xs space-y-1">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-violet-400" />
                        <span className="truncate">{wo.pickup_address || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-orange-400" />
                        <span className="truncate">{wo.drop_address || 'N/A'}</span>
                      </div>
                    </div>

                    {devicePos && (
                      <div className="mt-2 flex items-center gap-3 text-xs">
                        <span className={`flex items-center gap-1 ${devicePos.isOnline ? 'text-green-400' : 'text-slate-500'}`}>
                          <div className={`w-2 h-2 rounded-full ${devicePos.isOnline ? 'bg-green-400' : 'bg-slate-500'}`}></div>
                          {devicePos.isOnline ? 'Online' : 'Offline'}
                        </span>
                        {features.temp?.enabled && devicePos.temp !== null && (
                          <span className={`flex items-center gap-1 ${tempStatus.color}`}>
                            <Thermometer className="w-3 h-3" />
                            {devicePos.temp}°C ({tempStatus.text})
                          </span>
                        )}
                        {devicePos.battery && (
                          <span className="text-amber-400 flex items-center gap-1">
                            <Battery className="w-3 h-3" />
                            {devicePos.battery}%
                          </span>
                        )}
                      </div>
                    )}

                    {devicePos?.capabilities?.rider_name && (
                      <div className="mt-2 text-xs text-yellow-400 flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        {devicePos.capabilities.rider_name} • {devicePos.capabilities.vehicle}
                      </div>
                    )}

                    {features.temp?.enabled && (
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-slate-500 text-xs">
                          Temp Range: {wo.temp_min}°C - {wo.temp_max}°C
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-600" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer 
            center={mapCenter} 
            zoom={12} 
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
            <MapCenterController center={mapCenter} />
            
            {/* Warehouse/Pickup locations */}
            {locations.filter(loc => loc.type === 'warehouse' || loc.location_type === 'warehouse').map(loc => (
              <Marker 
                key={`loc-${loc.id}`}
                position={[parseFloat(loc.latitude), parseFloat(loc.longitude)]}
                icon={ICONS.warehouse}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-bold text-violet-600">🏭 Warehouse</p>
                    <p className="font-medium">{loc.name}</p>
                    <p className="text-slate-600">{loc.address}</p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Client/Store/Hospital locations */}
            {locations.filter(loc => loc.type !== 'warehouse' && loc.location_type !== 'warehouse').map(loc => (
              <Marker 
                key={`client-${loc.id}`}
                position={[parseFloat(loc.latitude), parseFloat(loc.longitude)]}
                icon={ICONS.client}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-bold text-orange-600">📍 Client</p>
                    <p className="font-medium">{loc.name}</p>
                    <p className="text-slate-600">{loc.address}</p>
                    <p className="text-xs text-slate-500 capitalize">{loc.type || loc.location_type}</p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* All devices with current positions */}
            {devicesWithPositions.map(device => {
              const temp = device.current_temp ? parseFloat(device.current_temp) : null;
              const isOnline = device.status === 'active' || device.status === 'online';
              const icon = getDeviceIcon(temp, isOnline);
              const tempStatus = getTempStatus(temp);
              const riderName = device.capabilities?.rider_name;
              const vehicle = device.capabilities?.vehicle;
              
              return (
                <Marker 
                  key={`device-${device.id}`}
                  position={[parseFloat(device.current_lat), parseFloat(device.current_lng)]}
                  icon={icon}
                >
                  <Popup>
                    <div className="text-sm min-w-[200px]">
                      <p className="font-bold text-lg">{device.device_uid}</p>
                      
                      {riderName && (
                        <p className="text-yellow-600 font-medium">
                          🚚 {riderName}
                        </p>
                      )}
                      {vehicle && (
                        <p className="text-slate-600 text-xs">{vehicle}</p>
                      )}
                      
                      <div className="mt-2 space-y-1">
                        {features.temp?.enabled && temp !== null && (
                          <p className={tempStatus.color}>
                            🌡️ Temperature: {temp}°C ({tempStatus.text})
                          </p>
                        )}
                        <p className="text-slate-600">
                          🔋 Battery: {device.battery_level || 'N/A'}%
                        </p>
                        <p className={isOnline ? 'text-green-600' : 'text-slate-500'}>
                          ● {isOnline ? 'Online' : 'Offline'}
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Work order routes */}
            {workOrders.map(wo => {
              const devicePos = getDevicePosition(wo.assigned_device_id);
              if (!devicePos) return null;
              
              const points = [];
              if (wo.pickup_lat && wo.pickup_lng) {
                points.push([parseFloat(wo.pickup_lat), parseFloat(wo.pickup_lng)]);
              }
              points.push([devicePos.lat, devicePos.lng]);
              if (wo.drop_lat && wo.drop_lng) {
                points.push([parseFloat(wo.drop_lat), parseFloat(wo.drop_lng)]);
              }
              
              if (points.length < 2) return null;
              
              return (
                <Polyline
                  key={`route-${wo.id}`}
                  positions={points}
                  color="#14b8a6"
                  weight={2}
                  opacity={0.6}
                  dashArray="5, 10"
                />
              );
            })}
          </MapContainer>

          {/* Legend */}
          <div className="absolute bottom-4 left-4 bg-white/95 dark:bg-slate-900/95 rounded-xl p-4 text-xs text-slate-700 dark:text-slate-300 shadow-lg border border-slate-200 dark:border-slate-700 z-[1000]">
            <p className="font-bold text-slate-900 dark:text-white mb-3 text-sm">Map Legend</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-violet-500"></div> Warehouse{tenant?.name ? ` (${tenant.name})` : ''}
              </span>
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-orange-500"></div> Client Location
              </span>
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500"></div> Device (Temp OK: 2-8°C)
              </span>
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div> Device (Too Hot &gt;8°C)
              </span>
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-500"></div> Device (Too Cold &lt;2°C)
              </span>
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-yellow-500"></div> Rider/Trip Active
              </span>
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-slate-500"></div> Device Offline
              </span>
            </div>
          </div>

          {/* Stats overlay */}
          <div className="absolute top-4 right-4 bg-white/95 dark:bg-slate-900/95 rounded-xl p-4 text-xs shadow-lg border border-slate-200 dark:border-slate-700 z-[1000]">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-green-500 dark:text-green-400">{devicesWithPositions.filter(d => {
                  const t = d.current_temp ? parseFloat(d.current_temp) : null;
                  return t !== null && t >= 2 && t <= 8;
                }).length}</p>
                <p className="text-slate-500 dark:text-slate-400">Temp OK</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-500 dark:text-red-400">{devicesWithPositions.filter(d => {
                  const t = d.current_temp ? parseFloat(d.current_temp) : null;
                  return t !== null && t > 8;
                }).length}</p>
                <p className="text-slate-500 dark:text-slate-400">Too Hot</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-500 dark:text-blue-400">{devicesWithPositions.filter(d => {
                  const t = d.current_temp ? parseFloat(d.current_temp) : null;
                  return t !== null && t < 2;
                }).length}</p>
                <p className="text-slate-500 dark:text-slate-400">Too Cold</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
